"use client"

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react"
import { usePathname } from "next/navigation"
import { X } from "lucide-react"
import Link from "next/link"
import { COMPANY_LOGO_BLUE } from "@/lib/constants"
import {
  POPUP_STORAGE_KEY,
  PRICE_WATCH_INLINE_KEY,
  dataIndexName,
  inlineWatchUsed,
  isExcludedPath,
  localStateName,
  parsePopupState,
  popupVariant,
  resolvePopupVariant,
  serializePopupState,
  shouldShowPopup,
  type PopupState,
  type PopupVariant,
  type PopupVariantId,
} from "@/lib/newsletter-popup-frequency"
import { usePopupCadence } from "@/components/use-popup-cadence"
import { isFrostyPanelOpen, subscribeFrostyPanelOpen } from "@/lib/homepage-2026/frosty-panel-state"

/**
 * Premium, penalty-safe, page-type-aware email capture.
 *
 * ONE system, deterministic variant selection by route (lib/newsletter-popup-
 * frequency → resolvePopupVariant). The card's shape + copy change per page
 * type; the frequency cap is shared across ALL variants.
 *
 * Variants (owner-directed sizing hierarchy: PDP largest · blog/data/recalls
 * medium · collections compact):
 *   • /products/*   PDP        LARGE   "Track this price?" → price_watch POST
 *                                       (product UUID read from the on-page
 *                                       DOM; respects the inline widget via
 *                                       PRICE_WATCH_INLINE_KEY — a user who
 *                                       used the inline affordance never sees
 *                                       this). Downgrades to the general
 *                                       newsletter card if no UUID is readable.
 *   • /blogs/*      blog       MEDIUM  price-drop / recall newsletter
 *   • /collections/* collection COMPACT category deal-alert bar
 *   • /data*        data       MEDIUM  index-update subscription
 *   • /recalls*     recalls    MEDIUM  recall-alert (calm, safety-toned)
 *   • /tools/*      tools       MEDIUM  price-drop alerts (calculator context)
 *   • /local/*      local       MEDIUM  state deal + rebate alerts
 *   • /financing*   financing   MEDIUM  finance-a-fridge deal alerts
 *   • everything    general    MEDIUM  general newsletter (homepage etc.)
 *
 * Penalty-safe invariants (apply to every variant — masterclass cadence lives
 * in lib/newsletter-popup-frequency + components/use-popup-cadence):
 *   • position:fixed → ZERO layout shift; never on first paint.
 *   • FRIDGE-780 — never before the page is interactive (hydrated + window
 *     `load`), then the FIRST real signal: 50% page scroll, 30 seconds on the
 *     page, or desktop exit-intent. See lib/newsletter-popup-frequency and
 *     components/use-popup-cadence. The card itself is unchanged (owner,
 *     FRIDGE-279: "popups stay as they are") — only the trigger moved.
 *   • Frequency-capped: one dismiss = 7-day cooldown on ALL variants, one
 *     subscribe = never again anywhere.
 *   • Excluded on contact + legal pages and the /r/ affiliate redirect path.
 *   • Never stacks with any open overlay (filter drawer or any aria-modal).
 *   • Mobile renders as a compact bottom banner regardless of variant.
 *   • Submits into the EXISTING /api/alerts/subscribe machinery, which fires
 *     the double-opt-in confirm email. Nothing arrives until the user confirms.
 */

// Brand orange (lib/constants BRAND_COLORS.orange) + its pressed/hover shade
// (matches the PDP + inline Price Watch CTA). Kept as literals so Tailwind's
// arbitrary-value JIT picks up the focus-ring utilities.
const ORANGE = "#ff9951"
const ORANGE_HOVER = "#ff8533"

type SubmitStatus = "idle" | "submitting" | "success" | "error"

/** Runtime, DOM-derived context resolved at reveal time. */
interface RuntimeCtx {
  /** effective variant after any PDP→general downgrade */
  variantId: PopupVariantId
  productId: string | null
  productTitle: string | null
  categoryLabel: string | null
  indexName: string | null
  stateName: string | null
  /** viewport was mobile at reveal → compact banner styling */
  isMobile: boolean
}

/** Pull the product UUID out of an on-page affiliate/redirect link
 *  (/r/<offer>?product_id=<uuid>&…). The PDP renders these anchors for every
 *  offer, so this needs no edit to the PDP files. */
function readProductIdFromDom(): string | null {
  if (typeof document === "undefined") return null
  const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
  const anchors = Array.from(document.querySelectorAll<HTMLAnchorElement>('a[href*="product_id="]'))
  for (const a of anchors) {
    const m = a.getAttribute("href")?.match(/product_id=([0-9a-f-]+)/i)
    if (m && UUID_RE.test(m[1])) return m[1]
  }
  return null
}

/** The product's on-page title (the PDP h1). */
function readProductTitleFromDom(): string | null {
  if (typeof document === "undefined") return null
  const h1 = document.querySelector("h1")?.textContent?.trim()
  return h1 && h1.length > 0 && h1.length <= 160 ? h1 : null
}

/** Human category label for the collections bar — the h1 if present, else a
 *  title-cased slug. */
function readCategoryLabel(pathname: string): string | null {
  if (typeof document !== "undefined") {
    const h1 = document.querySelector("h1")?.textContent?.trim()
    if (h1 && h1.length > 0 && h1.length <= 60) return h1
  }
  const slug = pathname.replace(/^\/collections\/?/, "").split("/")[0]
  if (!slug || slug === "all") return null
  return slug
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/** State name for the /local variant — the state page's `.place` eyebrow if
 *  present, else the title-cased slug from the route. */
function readLocalStateName(pathname: string): string | null {
  if (typeof document !== "undefined") {
    const place = document.querySelector(".place")?.textContent?.trim()
    if (place && place.length > 0 && place.length <= 40) return place
  }
  return localStateName(pathname)
}

interface VariantCopy {
  eyebrow: string
  title: string
  body: string
  cta: string
  placeholder: string
}

function copyFor(ctx: RuntimeCtx): VariantCopy {
  switch (ctx.variantId) {
    case "pdp": {
      const name = ctx.productTitle ?? "this refrigerator"
      return {
        eyebrow: "Price Watch",
        title: "Want to know when the price drops?",
        body: `We'll email you the moment ${name} drops in price — and no sooner. Unsubscribe anytime.`,
        cta: "Track price",
        placeholder: "you@email.com",
      }
    }
    case "collection": {
      const cat = ctx.categoryLabel
      return {
        eyebrow: "Deal alerts",
        title: cat ? `Get ${cat} price-drop alerts` : "Get refrigerator price-drop alerts",
        body: "Occasional, unsubscribe anytime.",
        cta: "Alert me",
        placeholder: "you@email.com",
      }
    }
    case "data": {
      const idx = ctx.indexName ?? "Appliance Price Index"
      return {
        eyebrow: "Data updates",
        title: `Follow the ${idx}`,
        body: `Get an email when we publish a fresh ${idx} update. Occasional, unsubscribe anytime.`,
        cta: "Email me updates",
        placeholder: "you@email.com",
      }
    }
    case "recalls":
      return {
        eyebrow: "Recall alerts",
        title: "Refrigerator recall alerts",
        body: "We'll email you if the CPSC announces a refrigerator recall we track. Safety-first, unsubscribe anytime.",
        cta: "Alert me",
        placeholder: "you@email.com",
      }
    case "tools":
      return {
        eyebrow: "Price alerts",
        title: "Done crunching the numbers?",
        body: "Get an email when the fridge or freezer you're sizing up drops in price — plus any safety recall we track. Occasional, unsubscribe anytime.",
        cta: "Email me price drops",
        placeholder: "you@email.com",
      }
    case "local": {
      const st = ctx.stateName
      return {
        eyebrow: "Local alerts",
        title: st ? `Fridge deals & rebates in ${st}` : "Local fridge deals & rebates",
        body: "Get an email when we spot a refrigerator price drop or rebate near you — plus safety recalls we track. Occasional, unsubscribe anytime.",
        cta: "Alert me",
        placeholder: "you@email.com",
      }
    }
    case "financing":
      return {
        eyebrow: "Deal alerts",
        title: "Financing a refrigerator?",
        body: "Get an email when we spot a price drop worth financing — plus any safety recall we track. Occasional, unsubscribe anytime.",
        cta: "Email me deals",
        placeholder: "you@email.com",
      }
    case "blog":
    case "general":
    default:
      return {
        eyebrow: "Price & recall alerts",
        title: "Never overpay for a fridge or freezer",
        body: "Get an email when we spot a fridge price drop or a safety recall. Occasional, unsubscribe anytime.",
        cta: "Email me alerts",
        placeholder: "you@email.com",
      }
  }
}

/** Card width + padding per the owner sizing hierarchy. */
function sizeClasses(variant: PopupVariant): { width: string; pad: string } {
  switch (variant.size) {
    case "large":
      return { width: "sm:w-[420px]", pad: "p-4 sm:p-6" }
    case "compact":
      return { width: "sm:w-[344px]", pad: "p-3 sm:p-4" }
    case "medium":
    default:
      return { width: "sm:w-[364px]", pad: "p-4 sm:p-5" }
  }
}

export default function NewsletterCapturePopup() {
  const pathname = usePathname()
  const baseVariant = useMemo(() => resolvePopupVariant(pathname), [pathname])

  const [mounted, setMounted] = useState(false) // drives the slide-in transition
  const [visible, setVisible] = useState(false)
  const [status, setStatus] = useState<SubmitStatus>("idle")
  const [email, setEmail] = useState("")
  const [ctx, setCtx] = useState<RuntimeCtx | null>(null)
  const honeypotRef = useRef("")
  // FRIDGE-222 — measured to size the reserved strip rendered below the card.
  const cardRef = useRef<HTMLDivElement | null>(null)
  const [reservedPx, setReservedPx] = useState(0)

  const persist = useCallback((patch: PopupState) => {
    try {
      const prev = parsePopupState(window.localStorage.getItem(POPUP_STORAGE_KEY))
      window.localStorage.setItem(POPUP_STORAGE_KEY, serializePopupState(prev, patch))
    } catch {
      /* storage blocked (private mode) — non-fatal */
    }
  }, [])

  // FRIDGE-797 — focus discipline for a NON-modal, cadence-revealed card:
  // the reveal is not a user request, so focus is never pulled into the card
  // (WCAG 3.2.5 Change on Request; a timed focus steal is what a screen-reader
  // user experiences as being yanked). Escape closes (below). When the card
  // closes while focus is INSIDE it (the visitor tabbed in, or typed the
  // email), focus returns to where it was before the reveal — never dropped
  // on <body>. aria-modal="false" stays truthful: nothing is trapped.
  const focusBeforeRevealRef = useRef<HTMLElement | null>(null)

  const dismiss = useCallback(() => {
    persist({ dismissedAt: Date.now() })
    const card = cardRef.current
    const active = typeof document !== "undefined" ? (document.activeElement as HTMLElement | null) : null
    if (card && active && card.contains(active)) {
      const back = focusBeforeRevealRef.current
      if (back && back.isConnected && typeof back.focus === "function") back.focus()
      else active.blur()
    }
    setVisible(false)
  }, [persist])

  // Eligibility snapshot, read when the cadence arms: shared frequency cap for
  // every variant + the PDP-only inline Price Watch suppression.
  const shouldArm = useCallback(() => {
    if (typeof window === "undefined" || !baseVariant) return false
    let allowed = true
    try {
      allowed = shouldShowPopup(parsePopupState(window.localStorage.getItem(POPUP_STORAGE_KEY)))
    } catch {
      allowed = true
    }
    if (!allowed) return false
    if (baseVariant.id === "pdp") {
      try {
        if (inlineWatchUsed(window.localStorage.getItem(PRICE_WATCH_INLINE_KEY))) return false
      } catch {
        /* storage blocked — treat as not-used */
      }
    }
    return true
  }, [baseVariant])

  // FRIDGE-1324 — the Frosty chat panel is a dialog without aria-modal (the
  // page stays usable beside it), so the overlay check below never saw it and
  // this card slid up over the panel's input row on product pages. The widget
  // publishes its open state; while it is open the reveal waits (cadence
  // intact: the poll below fires the moment the panel closes), and a card
  // that was already on screen steps out of sight and out of the pointer's way.
  const heldForChat = useSyncExternalStore(subscribeFrostyPanelOpen, isFrostyPanelOpen, () => false)

  // Never stack: block while the mobile filter drawer OR any other modal
  // overlay is open. Our own card is aria-modal="false", so it never matches.
  const isBlocked = useCallback(() => {
    if (typeof document === "undefined") return false
    if (document.body.dataset.filterDrawerOpen === "true") return true
    // FRIDGE-1324: nor while the Frosty chat panel is open.
    if (isFrostyPanelOpen()) return true
    // FRIDGE-611: nor while the preferred-source prompt is showing.
    return document.querySelector('[aria-modal="true"], dialog[open], [data-preferred-source-prompt]') != null
  }, [])

  // Resolve DOM-derived context at reveal (the page is fully painted by now).
  const onReveal = useCallback(
    ({ isMobile }: { isMobile: boolean }) => {
      if (!baseVariant) return
      let variantId: PopupVariantId = baseVariant.id
      let productId: string | null = null
      let productTitle: string | null = null
      let categoryLabel: string | null = null
      let indexName: string | null = null
      let stateName: string | null = null

      if (baseVariant.id === "pdp") {
        productId = readProductIdFromDom()
        productTitle = readProductTitleFromDom()
        // No resolvable product UUID → cannot POST price_watch. Downgrade to
        // the general newsletter card so the page still gets a (valid) ask.
        if (!productId) variantId = "general"
      } else if (baseVariant.id === "collection") {
        categoryLabel = readCategoryLabel(pathname as string)
      } else if (baseVariant.id === "data") {
        indexName = dataIndexName(pathname)
      } else if (baseVariant.id === "local") {
        stateName = readLocalStateName(pathname as string)
      }

      setCtx({ variantId, productId, productTitle, categoryLabel, indexName, stateName, isMobile })
      // Remember where focus was so dismiss() can hand it back (FRIDGE-797).
      focusBeforeRevealRef.current =
        typeof document !== "undefined" && document.activeElement !== document.body
          ? (document.activeElement as HTMLElement | null)
          : null
      setVisible(true)
      // The slide-in is a one-frame transition from translate-y-4/opacity-0.
      // Under prefers-reduced-motion the card appears in place instead: no
      // deferred frame, so nothing moves (the class list also carries
      // motion-reduce:transition-none as the CSS-side guarantee).
      if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
        setMounted(true)
      } else {
        requestAnimationFrame(() => setMounted(true))
      }
    },
    [baseVariant, pathname],
  )

  usePopupCadence({
    pathname,
    variantId: baseVariant?.id ?? null,
    active: Boolean(baseVariant) && !isExcludedPath(pathname),
    shouldArm,
    isBlocked,
    onReveal,
  })

  // ESC to dismiss while open.
  useEffect(() => {
    if (!visible) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [visible, dismiss])

  /**
   * FRIDGE-222 — reserve the space this panel occupies, so it never sits on
   * top of something the visitor needs to click.
   *
   * Below the `sm` breakpoint this card is `left-4 right-4` — full width,
   * pinned to the bottom, ~175px tall. Measured on /brands/cooler-depot at
   * 483px wide, the catalog's pagination control (rect 831-871) sat ENTIRELY
   * inside the panel's rect (778-953). The pagination carries no z-index of
   * its own, so the panel won and swallowed the clicks: page 2 of any brand
   * or collection catalog was unreachable on a phone — the surface the
   * affiliate clickout lives on. Blocked at 375 and 483, clear at 768.
   *
   * Raising the pagination's z-index would put it visually on top of this
   * panel, which is worse. A fixed bottom bar has one correct answer: reserve
   * its height in normal flow so everything can be scrolled clear of it. The
   * reservation is a rendered SPACER (see the render below), not a write to
   * `document.body` — an imperative style write is invisible in the DOM, so
   * a failure looks identical to a fix that works.
   *
   * Keyed on `mounted` and `ctx` as well as `visible`: the card only renders
   * once all three are set, so keying on `visible` alone can run before the
   * ref attaches and then never re-run.
   */
  useEffect(() => {
    if (!visible || !ctx || !mounted) return
    const el = cardRef.current
    if (!el) return
    // +16px matches the `bottom-4` offset, so the strip is the panel plus
    // the gap it floats above.
    const measure = () => setReservedPx(Math.ceil(el.getBoundingClientRect().height) + 16)
    measure()
    // Height changes with viewport width (copy rewraps) and with the panel's
    // own state — a validation message adds a line.
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null
    ro?.observe(el)
    window.addEventListener("resize", measure)
    return () => {
      ro?.disconnect()
      window.removeEventListener("resize", measure)
      setReservedPx(0)
    }
  }, [visible, ctx, mounted])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (honeypotRef.current) {
      setVisible(false)
      return
    }
    if (!ctx) return
    setStatus("submitting")

    // Effective variant descriptor (post-downgrade) drives the POST type.
    const variant = popupVariant(ctx.variantId)
    const isPriceWatch = variant.subscribeType === "price_watch" && Boolean(ctx.productId)

    const payload: Record<string, unknown> = isPriceWatch
      ? {
          type: "price_watch",
          email,
          generalUpdatesOptIn: true,
          productId: ctx.productId,
          frequency: "on_drop",
          honeypot: honeypotRef.current,
        }
      : {
          type: "newsletter",
          email,
          generalUpdatesOptIn: true,
          source: variant.source ?? "popup",
          honeypot: honeypotRef.current,
        }

    try {
      const res = await fetch("/api/alerts/subscribe", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      })
      const data = await res.json().catch(() => null)
      if (res.ok && data?.ok) {
        setStatus("success")
        persist({ subscribedAt: Date.now() }) // never show any variant again
      } else {
        setStatus("error")
      }
    } catch {
      setStatus("error")
    }
  }

  if (!visible || !ctx) return null

  const variant = popupVariant(ctx.variantId)
  const { width, pad } = sizeClasses(variant)
  const copy = copyFor(ctx)
  // Mobile always renders as a compact bottom banner, regardless of the
  // variant's desktop size (masterclass mobile cadence).
  const isCompact = variant.size === "compact" || ctx.isMobile
  const titleId = "nl-popup-title"

  return (
    <>
    {/* FRIDGE-222: the full-width mobile panel needs scroll clearance so
        pagination and final links remain reachable. At sm the panel floats
        in the corner; reserving its height there adds a blank region after
        the footer (FRIDGE-1016). Match the panel's sm:left-auto breakpoint. */}
    {/* FRIDGE-1111 (owner: "This should NOT Be possible"): this spacer renders AFTER the
        footer, so on a phone it was a 200px pure-white block below the grey footer.
        It carries the footer's colour now — the reserved scroll space reads as footer. */}
    <div aria-hidden="true" data-testid="newsletter-popup-spacer" className="sm:hidden bg-gray-50" style={{ height: reservedPx }} />
    <div
      ref={cardRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      data-testid="newsletter-capture-popup"
      data-variant={ctx.variantId}
      data-size={variant.size}
      data-compact={isCompact ? "true" : "false"}
      data-held-for-chat={heldForChat ? "true" : "false"}
      className={[
        // FRIDGE-781 — this dialog and the home page's chat pill both live in
        // the bottom-right corner, and the pill carries z-index 1000 (its
        // panel 1001) while this sat at z-40. The pill therefore floated ON
        // TOP of the dialog on desktop, over its text and within a few pixels
        // of its dismiss control. A dialog is never underneath a floating
        // launcher: 1002 puts it above both the pill and the pill's own panel,
        // and still below the site header's mobile menu (9998/9999), which is
        // a full-screen surface the shopper opened deliberately.
        "fixed z-[1002] bottom-4 right-4 left-4",
        "sm:left-auto",
        width,
        "rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden",
        // FRIDGE-1324 — transform and opacity only: `transition-all` also eased
        // `visibility`, which kept the card fully visible over the chat panel for
        // the whole 300 ms before the hidden state took effect. The slide-in is
        // unchanged; the hand-off to the chat is instant.
        "transition-[transform,opacity] duration-300 ease-out motion-reduce:transition-none",
        mounted ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0",
        // FRIDGE-1324 — out of sight and untouchable while the chat panel is open;
        // state, cadence and the typed email survive, and it returns on close.
        heldForChat ? "invisible pointer-events-none" : "",
      ].join(" ")}
    >
      {/* Brand accent bar (orange) — the mark of the card. */}
      <div
        aria-hidden="true"
        className="h-1 w-full"
        style={{ background: `linear-gradient(90deg, ${ORANGE} 0%, ${ORANGE_HOVER} 100%)` }}
      />

      <div className={pad}>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute top-4 right-3 text-gray-500 hover:text-gray-700 rounded-md p-1"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Brand logo mark (the header wordmark asset). Fixed height → no CLS. */}
        <div className="flex items-center gap-2 pr-6">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={COMPANY_LOGO_BLUE}
            alt="Fridge.com"
            className={isCompact ? "h-4 w-auto" : "h-5 w-auto"}
            width={120}
            height={20}
          />
          <span
            className="text-[10px] font-bold uppercase tracking-wide"
            style={{ color: ORANGE_HOVER }}
          >
            {copy.eyebrow}
          </span>
        </div>

        {status === "success" ? (
          <div data-state="success" className="mt-3">
            <p className="text-[15px] font-semibold text-gray-900">Check your inbox to confirm.</p>
            <p className="mt-1 text-[13px] leading-relaxed text-gray-600">
              We just emailed you a confirmation link. Click it and you&apos;re set — nothing
              arrives until you confirm.
            </p>
          </div>
        ) : (
          <>
            <p
              id={titleId}
              className={[
                "mt-2 font-bold text-gray-900 pr-6 tracking-tight",
                variant.size === "large" ? "text-[15px] sm:text-[17px]" : isCompact ? "text-[14px]" : "text-[14px] sm:text-[15px]",
              ].join(" ")}
            >
              {copy.title}
            </p>
            {!isCompact && (
              <p className="mt-1 text-[13px] leading-relaxed text-gray-600 hidden sm:block">
                {copy.body}{" "}
                {/* FRIDGE-146: notice at collection on the broadest email form — the same
                    markup the price-watch form uses. */}
                <Link href="/privacy-policy" className="underline hover:text-gray-700">
                  Privacy policy
                </Link>
              </p>
            )}
            {isCompact && (
              <p className="mt-1 text-[11px] leading-relaxed text-gray-500">
                Unsubscribe anytime.{" "}
                <Link href="/privacy-policy" className="underline hover:text-gray-700">
                  Privacy policy
                </Link>
              </p>
            )}

            <form onSubmit={onSubmit} className={isCompact ? "mt-2.5" : "mt-3"}>
              {/* Honeypot: hidden field real users never fill. */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
                onChange={(e) => {
                  honeypotRef.current = e.target.value
                }}
              />
              <label htmlFor="nl-popup-email" className="sr-only">
                Email address
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  id="nl-popup-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={copy.placeholder}
                  disabled={status === "submitting"}
                  className="flex-1 min-w-0 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#ff9951] focus:ring-2 focus:ring-[#ff9951]/25"
                />
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  style={{ backgroundColor: ORANGE }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = ORANGE_HOVER
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = ORANGE
                  }}
                  className="rounded-lg disabled:opacity-100 px-4 py-2 text-sm font-semibold text-[var(--color-brand-action-text)] whitespace-nowrap transition-colors disabled:cursor-not-allowed"
                >
                  {status === "submitting" ? "Sending…" : copy.cta}
                </button>
              </div>
              {status === "error" && (
                <p className="mt-2 text-xs text-red-600">
                  Something went wrong. Please check your email and try again.
                </p>
              )}
            </form>
          </>
        )}
      </div>
    </div>
    </>
  )
}
